import { useEffect, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import { versionDe, type ReglaReserva as DatosRegla, type Reservable, type ValoresRegla } from '../../api/ops/disponibilidad';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { fechaLarga, hoyEn, sumarDias } from '../../logica/horario';
import { textoDeErrorApi } from './cambios';
import { AYUDA_REGLA } from './textos';
import { CORTE_OPC, conActual, diferencias, MAX_OPC, MIN_OPC, PASO_OPC, textoMax, textoMin, TIPOS, valoresDe, type MismoDia, type Valores } from './regla';
import './horario.css';

/**
 * 3.3 «Cuándo se puede reservar» (en citas, «Cuándo se puede pedir cita»): la regla general del negocio
 * (`target_kind: business`), en la tarjeta de «Negocio › Datos del negocio» con su RV Panel (`?pantalla=antelacion`).
 * Arriba la regla en una frase con los valores dentro y un ejemplo con la fecha de hoy; debajo «Como pronto», «Como
 * tarde», «El mismo día» y, plegado, cada cuánto empieza un hueco (componentes.md §5f). Plegadas, las zonas y recursos con
 * su propia regla: solo los nombres, se cambian en su ficha (Recursos). Sin regla por profesional (G252). Valores por
 * defecto, los de G256 (60 minutos, hasta la hora del hueco y 90 días). Se guarda con `updateBookingRule` e `If-Match`.
 */
export interface ReglaConVersion {
    datos: DatosRegla;
    version: string;
}

interface Props {
    ops: Operaciones;
    negocio: string;
    abierto: boolean;
    onCerrar: () => void;
    movil: boolean;
    editable: boolean;
    citas: boolean;
    zona: string;
    regla: ReglaConVersion | null;
    onGuardada: (r: ReglaConVersion) => void;
    onRecargar: () => void;
}

export function ReglaReserva({ ops, negocio, abierto, onCerrar, movil, editable, citas, zona, regla, onGuardada, onRecargar }: Props) {
    const avisar = useAvisoBreve();
    const [v, setV] = useState<Valores>(() => valoresDe(regla?.datos));
    const [ocupado, setOcupado] = useState(false);
    const [propias, setPropias] = useState<{ regla: DatosRegla; reservable: Reservable }[]>([]);

    // Al abrir (o si cambia la regla guardada), los valores guardados.
    const datos = regla?.datos;
    const [visto, setVisto] = useState({ abierto, datos });
    if (visto.abierto !== abierto || visto.datos !== datos) {
        setVisto({ abierto, datos });
        if (abierto) {
            setV(valoresDe(datos));
        }
    }

    // Lo que tiene regla propia: solo los nombres de zonas y recursos.
    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vivo = true;
        Promise.all([ops.listBookingRules(negocio, { target_kind: 'bookable', limit: 100 }), ops.listBookables(negocio, { limit: 100 })])
            .then(([r, b]) => {
                if (!vivo) {
                    return;
                }
                const lista = r.data.flatMap((x) => {
                    const reservable = b.data.find((y) => y.id === x.bookable_id);
                    return reservable !== undefined && reservable.type in TIPOS && reservable.status === 'active' ? [{ regla: x, reservable }] : [];
                });
                setPropias(lista);
            })
            .catch(() => {
                if (vivo) {
                    setPropias([]);
                }
            });
        return () => {
            vivo = false;
        };
    }, [abierto, ops, negocio]);

    const verbo = citas ? 'pedir cita' : 'reservar';
    const de = citas ? 'la cita' : 'la reserva';
    const hoy = hoyEn(zona);
    const anio = Number(hoy.slice(0, 4));
    const frase = `El mismo día, ${v.mismoDia === 'until_slot' ? `hasta la hora de ${de}` : v.mismoDia === 'minutes_before' ? `hasta ${textoMin(v.corte)} antes de ${de}` : 'solo antes de abrir'}.`;
    const pasoTexto = v.paso === null ? 'sin elegir' : (PASO_OPC.find(([p]) => p === v.paso)?.[1] ?? `Cada ${textoMin(v.paso)}`).toLocaleLowerCase('es');

    const guardar = () => {
        if (regla === null) {
            return;
        }
        setOcupado(true);
        const valores: ValoresRegla = {
            min_advance_minutes: v.min,
            same_day_mode: v.mismoDia,
            same_day_cutoff_minutes: v.mismoDia === 'minutes_before' ? v.corte : null,
            max_advance_days: v.max,
            // G853: el paso de la rejilla es de solo lectura en el panel (lo cambia Soporte); no se envía.
        };
        ops.updateBookingRule(negocio, regla.datos.id, valores, regla.version)
            .then((r) => {
                onGuardada({ datos: r.datos, version: r.version ?? versionDe(r.datos) });
                avisar({ tipo: 'ok', texto: 'Cambios guardados.' });
                onCerrar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: 'Alguien ha cambiado esta regla mientras tanto. La he vuelto a cargar: revisa y guarda otra vez.' });
                    onRecargar();
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeErrorApi(e, 'No he podido guardar. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const opciones = (lista: readonly [number, string][]) => lista.map(([valor, texto]) => ({ valor: String(valor), texto }));
    const bloqueado = !editable || regla === null;
    const nombres = propias.map((p) => p.reservable.name ?? '').filter((n) => n !== '');
    const listaNombres = nombres.length < 2 ? nombres.join('') : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1] ?? ''}`;

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            titulo={citas ? 'Cuándo se puede pedir cita' : 'Cuándo se puede reservar'}
            eyebrow="Regla general"
            subtitulo={citas ? 'Para todas las citas.' : 'Para todo lo que se reserva.'}
            icono="hourglass"
            ayuda={AYUDA_REGLA(citas)}
            accion={editable && regla !== null ? 'Guardar' : ''}
            ocupado={ocupado}
            onAccion={guardar}
        >
            <div className="rv-neg-campos">
                <div className="rv-regla">
                    <p className="rv-regla__frase">
                        {`Se puede ${verbo} desde `}
                        <span>{textoMax(v.max)}</span>
                        {v.min === 0 ? ' antes hasta el último momento.' : ' antes hasta '}
                        {v.min !== 0 && <span>{textoMin(v.min)}</span>}
                        {v.min !== 0 && ' antes.'}
                    </p>
                    <p className="rv-regla__dia">{frase}</p>
                    <p className="rv-regla__ejemplo">
                        <Icono nombre="calendar-clock" />
                        <span>{`Por ejemplo, hoy (${fechaLarga(hoy, anio)}) se puede ${verbo} hasta el ${fechaLarga(sumarDias(hoy, v.max), anio)}.`}</span>
                    </p>
                </div>
                <Desplegable
                    etiqueta="Como pronto"
                    valor={String(v.max)}
                    opciones={opciones(conActual(MAX_OPC, v.max, (n) => `${textoMax(n)} antes`))}
                    bloqueado={bloqueado}
                    ayuda={`Lo más lejos que se puede ${verbo}.`}
                    onChange={(x) => {
                        setV((y) => ({ ...y, max: Number(x) }));
                    }}
                />
                <Desplegable
                    etiqueta="Como tarde"
                    valor={String(v.min)}
                    opciones={opciones(conActual(MIN_OPC, v.min, (n) => `${textoMin(n)} antes`))}
                    bloqueado={bloqueado}
                    ayuda="Para cualquier día menos hoy."
                    onChange={(x) => {
                        setV((y) => ({ ...y, min: Number(x) }));
                    }}
                />
                <Desplegable
                    etiqueta="El mismo día"
                    valor={v.mismoDia}
                    opciones={[
                        { valor: 'until_slot', texto: `Hasta la hora de ${de}` },
                        { valor: 'minutes_before', texto: 'Hasta unos minutos antes' },
                        { valor: 'until_opening', texto: 'Solo antes de abrir' },
                    ]}
                    bloqueado={bloqueado}
                    ayuda={`Hasta cuándo se puede ${verbo} para hoy.`}
                    onChange={(x) => {
                        setV((y) => ({ ...y, mismoDia: x as MismoDia }));
                    }}
                />
                {v.mismoDia === 'minutes_before' && (
                    <Desplegable
                        etiqueta="Cuánto antes"
                        valor={String(v.corte)}
                        opciones={opciones(conActual(CORTE_OPC, v.corte, (n) => `${textoMin(n)} antes`))}
                        bloqueado={bloqueado}
                        onChange={(x) => {
                            setV((y) => ({ ...y, corte: Number(x) }));
                        }}
                    />
                )}
                <details className="rv-hor-plegable">
                    <summary>
                        <span className="rv-hor-plegable__baldosa">
                            <Icono nombre="sliders-horizontal" />
                        </span>
                        <span className="rv-hor-plegable__texto">
                            <strong>Más opciones</strong>
                            <span>{`Las horas que se ofrecen van ${pasoTexto}`}</span>
                        </span>
                        <Icono nombre="chevron-down" />
                    </summary>
                    <div className="rv-hor-plegable__cuerpo">
                        <Desplegable
                            etiqueta={citas ? 'Cada cuánto empieza una cita' : 'Cada cuánto empieza una reserva'}
                            valor={v.paso === null ? '' : String(v.paso)}
                            opciones={[...(v.paso === null ? [{ valor: '', texto: 'Sin elegir' }] : []), ...opciones(v.paso === null ? PASO_OPC : conActual(PASO_OPC, v.paso, (n) => `Cada ${textoMin(n)}`))]}
                            bloqueado
                            ayuda={`${v.paso === null ? '' : v.paso === 60 ? 'Por ejemplo: 21:00, 22:00, 23:00… ' : `Por ejemplo: 21:00, 21:${String(v.paso).padStart(2, '0')}… `}Mueve todas las horas que se ofrecen: lo cambia Soporte de Reservaria.`}
                            onChange={() => undefined}
                        />
                    </div>
                </details>
                {/* En citas, la regla propia es de cada servicio (Servicios 9.4); sin `listServices` no hay nombres que enseñar. */}
                {!citas && propias.length > 0 && (
                    <details className="rv-hor-plegable">
                        <summary>
                            <span className="rv-hor-plegable__baldosa">
                                <Icono nombre="layers" />
                            </span>
                            <span className="rv-hor-plegable__texto">
                                <strong>Zonas con su propia regla</strong>
                                <span>{listaNombres}</span>
                            </span>
                            <Icono nombre="chevron-down" />
                        </summary>
                        <div className="rv-hor-plegable__cuerpo">
                            {propias.map((p) => (
                                <div key={p.regla.id} className="rv-hor-cierre">
                                    <span className="rv-ca-baldosa">
                                        <Icono nombre="layers" />
                                    </span>
                                    <span className="rv-ca-fila__texto">
                                        <strong>{p.reservable.name ?? ''}</strong>
                                        <span>{[TIPOS[p.reservable.type] ?? '', diferencias(p.regla, citas)].filter((x) => x !== '').join(' · ')}</span>
                                    </span>
                                </div>
                            ))}
                            <span className="rv-hor-tramo__nota">Lo demás sigue esta regla. La de cada zona o recurso se cambia en su ficha (Recursos).</span>
                        </div>
                    </details>
                )}
                <p className="rv-ca-nota">
                    <Icono nombre="info" />
                    <span>{citas ? 'Vale para la web, el widget, el chat, el panel y la tablet. Las citas ya pedidas no cambian.' : 'Vale para la web, el widget, el chat, el panel y la tablet. Lo ya reservado no cambia.'}</span>
                </p>
            </div>
        </PanelLateral>
    );
}
